import { SignupPage } from '../features/auth/SignupPage.tsx';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { useActiveBusiness, useAuthStore } from './auth-store.ts';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage.tsx';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage.tsx';
import { InvitePage } from '../features/auth/InvitePage.tsx';
import { SelectBusinessPage } from '../features/auth/SelectBusinessPage.tsx';
import { AppLayout } from '../features/shell/AppLayout.tsx';
import { ForbiddenPage } from '../features/shell/ForbiddenPage.tsx';
import { PlaceholderPage } from '../features/placeholder/PlaceholderPage.tsx';
import { MySummaryPage } from '../features/summary/MySummaryPage.tsx';
import { WaitlistPage } from '../features/waitlist/WaitlistPage.tsx';
import { AuditPage } from '../features/audit/AuditPage.tsx';
import { PlanPage } from '../features/plan/PlanPage.tsx';
import { ReportsPage } from '../features/reports/ReportsPage.tsx';
import { ActivationPage } from '../features/onboarding/OnboardingPage.tsx';
import { HelpPage, TutorialPage } from '../features/help/HelpPage.tsx';
import { ProfilePage } from '../features/auth/ProfilePage.tsx';
import { CalendarPage } from '../features/calendar/CalendarPage.tsx';
import { ClientsPage } from '../features/clients/ClientsPage.tsx';
import { ClientPage } from '../features/clients/ClientPage.tsx';
import { ConversationsPage } from '../features/conversations/ConversationsPage.tsx';
import { WhatsAppPage } from '../features/config/WhatsAppPage.tsx';
import { MessagesPage } from '../features/config/MessagesPage.tsx';
import { TestModePage } from '../features/config/TestModePage.tsx';
import { AiPage, BusinessPage, HomeServicePage, HoursPage, ResourcesPage, ServicesPage, TeamPage } from '../features/config/index.ts';

function Boot() {
  return <div className="k-auth"><span className="k-spinner" aria-label="loading" /></div>;
}

function RequireAuth() {
  const { status, businessId } = useAuthStore();
  if (status === 'booting') return <Boot />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  if (!businessId) return <Navigate to="/select-business" replace />;
  return <Outlet />;
}

function RequireSession() {
  const status = useAuthStore((s) => s.status);
  if (status === 'booting') return <Boot />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return <Outlet />;
}

/** Telas cujas rotas da API exigem admin (config.routes requireAdmin, relatórios, auditoria): usuário padrão vê "sem permissão". */
function RequireAdminRole() {
  const business = useActiveBusiness();
  if (business && business.role !== 'ADMIN') return <ForbiddenPage />;
  return <Outlet />;
}

function AnonymousOnly() {
  const status = useAuthStore((s) => s.status);
  if (status === 'booting') return <Boot />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <AnonymousOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/cadastro', element: <SignupPage /> },
    ],
  },
  { path: '/invite', element: <InvitePage /> },
  {
    element: <RequireSession />,
    children: [{ path: '/select-business', element: <SelectBusinessPage /> }],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/agenda" replace /> },
          { path: 'agenda', element: <CalendarPage /> },
          { path: 'conversas', element: <ConversationsPage /> },
          { path: 'fila', element: <WaitlistPage /> },
          { path: 'clientes', element: <ClientsPage /> },
          { path: 'clientes/:id', element: <ClientPage /> },
          { path: 'config/meus-horarios', element: <HoursPage /> },
          { path: 'config/meu-resumo', element: <MySummaryPage /> },
          {
            element: <RequireAdminRole />,
            children: [
              { path: 'relatorios', element: <ReportsPage /> },
              { path: 'auditoria', element: <AuditPage /> },
              { path: 'config/negocio', element: <BusinessPage /> },
              { path: 'config/ia', element: <AiPage /> },
              { path: 'config/servicos', element: <ServicesPage /> },
              { path: 'config/recursos', element: <ResourcesPage /> },
              { path: 'config/horarios', element: <HoursPage /> },
              { path: 'config/equipe', element: <TeamPage /> },
              { path: 'config/domicilio', element: <HomeServicePage /> },
              { path: 'config/whatsapp', element: <WhatsAppPage /> },
              { path: 'config/mensagens', element: <MessagesPage /> },
              { path: 'config/plano', element: <PlanPage /> },
              { path: 'ativacao', element: <ActivationPage /> },
              { path: 'config/teste', element: <TestModePage /> },
            ],
          },
          { path: 'config/:section', element: <PlaceholderPage pageKey="config" /> },
          { path: 'perfil', element: <ProfilePage /> },
          { path: 'ajuda', element: <HelpPage /> },
          { path: 'ajuda/tutorial/:slug', element: <TutorialPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
